import { Check } from 'lucide-react';
import healthDashboard from '@/assets/illustrations/health-dashboard.png';
import brainEngagement from '@/assets/illustrations/brain-engagement.png';
import familyConnection from '@/assets/illustrations/family-connection.png';
import practicalHelp from '@/assets/illustrations/practical-help.png';

interface FeatureBlock {
  headline: string;
  body: string;
  bullets: string[];
  image: string;
  imageAlt: string;
}

const featureBlocks: FeatureBlock[] = [
  {
    headline: "Your Health. In Your Pocket.",
    body: "Stop wondering if you're \"okay.\" Track your vitals in seconds so you—and your doctor—have the full picture.",
    bullets: [
      "Track blood pressure, weight, and sleep trends",
      "Manage medications with smart reminders",
      "Get a simple \"ThriveScore\" to see how you're doing"
    ],
    image: healthDashboard,
    imageAlt: "Wellness dashboard showing health metrics"
  },
  {
    headline: "Stay Sharp. Have Fun.",
    body: "An active mind is a healthy mind. Daily reasons to engage that aren't just \"medical.\"",
    bullets: [
      "Play cognitive games to keep your brain vibrant",
      "Explore new hobbies and content",
      "Store cherished memories in your private gallery"
    ],
    image: brainEngagement,
    imageAlt: "Brain games and engaging activities"
  },
  {
    headline: "Connected. Not Watched.",
    body: "Stay close to the people you love without giving up your privacy. You control the relationship.",
    bullets: [
      "Share what you want—or keep it private",
      "Video chat with family directly from your browser",
      "24/7 friendly chat companions for guidance"
    ],
    image: familyConnection,
    imageAlt: "Family members connected through video chat"
  },
  {
    headline: "Real Help for Daily Life.",
    body: "Independence means handling your business. All the resources you need, in one place.",
    bullets: [
      "Order food or find nutritious recipes",
      "Securely store important documents",
      "Find transportation or pet care help instantly"
    ],
    image: practicalHelp,
    imageAlt: "Daily life support including documents and transportation"
  }
];

const FeatureBlocksSection = () => {
  return (
    <section className="py-8">
      <div className="space-y-20 lg:space-y-28">
        {featureBlocks.map((block, index) => {
          const isReversed = index % 2 !== 0;
          
          return (
            <div 
              key={index} 
              className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
            >
              {/* Content */}
              <div className={`space-y-5 ${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                <h2 className="text-2xl lg:text-3xl font-bold text-foreground tracking-tight">
                  {block.headline}
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {block.body}
                </p>
                <ul className="space-y-3 pt-1">
                  {block.bullets.map((bullet, bulletIndex) => (
                    <li key={bulletIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-primary" strokeWidth={3} />
                      </div>
                      <span className="text-foreground/80">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Image */}
              <div className={`${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-br from-primary/8 to-transparent rounded-3xl blur-xl" />
                  <img
                    src={block.image}
                    alt={block.imageAlt}
                    className="relative w-full h-auto max-h-[380px] object-contain rounded-2xl"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FeatureBlocksSection;
